//#region src/audit-vocabulary.ts
/**
* The value list of {@link AuditActorType}, for a `z.enum` at a boundary. Declared beside the
* union with `satisfies` so adding a member to one without the other fails the typecheck. Added
* for `@zion/sdk-dp-admin-contracts` (ruling R-27 of the admin-app extraction).
*/
const AUDIT_ACTOR_TYPES = [
	"human",
	"agent",
	"worker",
	"service",
	"external",
	"unknown"
];
/** The value list of {@link AuditLogSource}; see {@link AUDIT_ACTOR_TYPES} for why it exists. */
const AUDIT_LOG_SOURCES = ["platform", "app"];
/**
* Verb pattern from the class-validator era - kebab + dot + dash, lowercase
* only, must start with a letter. Mirrors `/^[a-z][a-z0-9._-]*$/` verbatim.
*
* ⛔ RENAMED FROM `ACTION_PATTERN` AND MOVED HERE, because it is now the grammar
* of TWO surfaces: the ingest route's `action`, and a manifest-declared verb. A
* JSON Schema cannot import a TypeScript constant, so `$defs/auditEvent.verb`
* carries the pattern as a literal and a test byte-compares the two. This is the
* TypeScript side of that comparison; there must never be a second one.
*/
const AUDIT_VERB_PATTERN = /^[a-z][a-z0-9._-]*$/;
/**
* The ladder, ASCENDING. Index order is the comparison order — `security` is the
* highest and `system` the lowest, so a floor of `admin` admits `admin` and
* `security`. Audit-native equivalent of DEBUG/INFO/WARN/ERROR.
*/
const AUDIT_LEVELS = [
	"system",
	"activity",
	"admin",
	"security"
];
/** The three-way actor selector exposed to callers. */
const AUDIT_ACTOR_FILTERS = [
	"all",
	"people",
	"system"
];
/**
* The kinds a related id may name (2026-10-06-audit-second-id-filter, D-4, D-9). A related id is
* the clear id of the second entity an audit row names in `details`, beside the row's object.
* Who calls it: core-api's envelope builder copies the declared id into the envelope's
* `relatedObjectId`, and its publisher reads the kind to decide whether the id joins the row's
* data subjects. The kind decides only that. The match itself is kind-blind: a `userId` read
* matches the related id whatever its kind (D-1).
*
* - `person`: a `users` id. The publisher adds `user:<id>` to the data subjects.
* - `principal`: a `machine_principals` id. The publisher adds `principal:<id>`.
* - `member`: a person or a machine principal; only the emit site knows which, so the emit site
*   declares the data subject (`user:` for a person, `principal:` otherwise). A key that can name
*   a person takes this kind or `person`, never `principal`, so that a person's erasure reaches
*   the row.
* - `grant_subject`: a person, a machine principal, a group, or a role. A grant subject of
*   kind `principal` (`RESOURCE_GRANT_SUBJECT_KINDS`) is a `users` id or a `machine_principals`
*   id. A role grant names the role twice: `subjectId` holds the role slug, and `orgRoleId` holds
*   the org role definition id. The role definition id is the related id of a role row, so the two
*   `resource_grant.*` types declare `orgRoleId` before `subjectId`. A group or a principal row
*   holds no `orgRoleId`, so its related id is its `subjectId`. The emit site declares
*   `user:<id>` when the subject is a person, `principal:<id>` when it is a machine principal,
*   and nothing for a group or a role.
* - `group`: a group id. No data subject.
* - `role`: an org role definition id. No data subject.
*
* The order is fixed and a new kind only appends.
*/
const AUDIT_RELATED_KINDS = [
	"person",
	"principal",
	"member",
	"grant_subject",
	"group",
	"role"
];
/**
* The default view: everything ranked `activity` or above, whoever acted.
*
* ⛔ THE `people` DIMENSION IS DELIBERATELY *NOT* IN THE DEFAULT, AND THE
* MEASUREMENT IS WHY. This read `admin` + `people` and returned **0 of 1,578
* rows** on `dp-app` (measured 2026-08-20, issue #1942). Both halves were
* individually defensible - "real administrative activity, performed by people"
* - and the intersection was empty, because:
*   1. NO event has ever fired at level `admin` in that org. An `admin` floor
*      and a `security`-only view both return the same 320 rows, so the whole
*      320 is security-level. 23 types are classified `admin` and not one has
*      been emitted.
*   2. All 320 security rows are machine-actor (`machine_principal.*`), so a
*      `people` filter correctly removes every one of them.
*
* ⇒ On a platform whose trail is dominated by machine principals, `people` is
* the WRONG axis to filter on by default: it hides exactly the security-relevant
* machine activity an operator opens this page to find.
*
* ⚠️ AND `system` + `people` IS NOT THE FIX EITHER - it returns 416 rows of
* which **413 are `token_refresh`** (99.3%, measured). That trades "empty" for
* "a page of token refreshes", which is worse: it LOOKS populated while
* carrying no signal. The `activity` floor is what excludes the two system-level
* dominators (`app.permission.invalid` 821 + `token_refresh` 415 = 78% of the
* table) while keeping every security, admin and activity row regardless of who
* acted. Measured result: 324 rows.
*
* ⛔ WIDENING THIS GRANTS NO NEW ACCESS. The caller is already org-scoped by RLS
* and already authorized for the whole table - "Any actor" was always one select
* away. This changes only what the first page shows.
*
* ⛔ THIS PACKAGE IS THE ONE DECLARATION, AND THE PAGE READS IT. `AuditLogPage`
* in `witness-ai/zion-admin-app` imports `DEFAULT_AUDIT_LEVEL` and
* `DEFAULT_AUDIT_ACTOR` from `@zion/sdk-contracts` for its initial `useState`,
* and it still sends both dimensions explicitly on the wire, so a change here
* alone still moves nothing a user sees until the app picks up the new version.
* The arm is `audit-default-view.test.tsx` beside that page, which asserts the
* rendered level and actor controls equal these two constants.
*
* ⚠️ THE PARITY NOW CROSSES A REPO BOUNDARY, AND THE VERSION PIN IS WHAT CARRIES
* IT. The app moved out of this monorepo on 2026-09-16
* (2026-09-16-admin-app-own-repo, D1/D3/D7), so no gate in either repo reads the
* other's tree: `audit-default-view.test.tsx` compares the page against the
* `@zion/sdk-contracts` version its own lockfile pins, so it stays green through
* a skew. A change to either constant reaches the page only when that repo bumps
* the pin. Ship the two together.
*/
const DEFAULT_AUDIT_LEVEL = "activity";
const DEFAULT_AUDIT_ACTOR = "all";
/**
* How an event type ABSENT from {@link AUDIT_EVENT_CLASSIFICATION} resolves.
*
* ⛔ FAIL-SAFE TOWARDS VISIBILITY, AND THIS DIRECTION IS LOAD-BEARING. A new
* event type added by a future change is unknown here until someone classifies
* it. Resolving it to the LOWEST level would hide it from the default view
* silently, and nothing would fail until an incident needed it — the exact
* failure mode an audit trail exists to prevent. It resolves to the HIGHEST
* level and to `people` instead, so an unclassified type is always visible in
* the default view. Noisy is recoverable; invisible is not.
*
* This is also what covers the internal-ingest passthrough, whose `action` is a
* caller-declared and therefore unbounded string: no enumeration could cover it.
*
* ✅ THE KNOWN-LOW RESIDUAL IS CLOSED, AND CLOSED SOMEWHERE ELSE (#1741
* finding 4, C-003). This paragraph used to record that the fail-safe covered
* the UNKNOWN case and not the KNOWN-LOW one: `POST /internal/audit` accepted
* any `action` matching its schema, so a caller could name its row after a
* `system`-level platform type, inherit that type's level, and sit below the
* default view. {@link isPlatformAuditVerb} now refuses an ingest `action`
* naming ANY member of {@link AUDIT_EVENT_CLASSIFICATION}, so such a verb is
* answered 400 at the route instead of accepted at a low level.
*
* ⛔ THE FIX IS A COLLISION PREDICATE, NOT A CLASSIFICATION BY `(source,
* action)` — which is what this paragraph used to propose, and it would have
* been the wrong move. Classifying by source keeps the ingest vocabulary
* unbounded and then overrides its level, so a legitimate app verb and an
* impersonating one still land in the same bucket. Refusing the collision
* leaves the verb UNKNOWN instead, which resolves here to `security`/`people`
* and is therefore always visible. Noisy is recoverable; invisible is not, and
* that is the same argument this whole block rests on.
*
* ⚠️ SO THIS FALLBACK IS NOW THE ONLY RESOLUTION AN INGEST VERB CAN GET, which
* makes the direction above load-bearing rather than merely prudent. Stage 3
* adds a second resolution for a DECLARED app verb, from the persisted
* registry; an undeclared one keeps landing here.
*
* ⚠️ Its `subject` is `varies`, not a kind and not `none`. An unclassified type
* is one nobody has ruled on, so asserting a subject KIND for it would assert
* something unmeasured — and asserting `none` would assert there is no subject,
* which is a stronger claim still.
*/
const UNKNOWN_EVENT_CLASS = {
	level: "security",
	actor: "people",
	destination: "audit",
	expectedActorType: "unknown",
	subject: "unset"
};
/**
* One entry per known event type.
*
* The ladder's own rules, so an assignment can be argued with rather than
* guessed at:
*   security — creates, rotates, revokes or replays a credential/token/key ·
*              changes an authorization allowlist or permission set ·
*              substitutes privilege · denies access
*   admin    — operator-driven change to org/user/app state that touches no
*              credential and no allowlist
*   activity — routine, successful, self-directed user activity
*   system   — no actor, but carries a security decision or a security residue
*
* And for the actor column: `system` when the acting principal is (or would be)
* machinery — a machine principal, a reconciler, a sweeper, an automatic roll,
* an IdP push, or a platform component. `people` otherwise, INCLUDING the
* operator-driven types whose actor plumbing is currently missing at the emit
* site. Those are human administrative actions with a missing column, not
* machine activity, and classifying them `system` would hide precisely the rows
* this page exists to show.
*
* ⛔ ON THE `subject` COLUMN: A `varies` SUBJECT IS A PLACEHOLDER, NOT A RULING.
* Every type carrying one is a type with NO emit site yet (45 of the 87
* audit-destined members when this contract landed), so no kind has been
* established for it and guessing one would assert something nobody measured.
* ⇒ EACH REMAINING EMIT-SITE BATCH IS EXPECTED TO TIGHTEN THE `varies` ENTRIES
* IT CONVERTS to the kind its sites actually write. Tightening is the point:
* `varies` widens {@link SubjectTypeFor} to `string | null`, so it accepts
* anything, while a pinned kind makes the COMPILER ENUMERATE every site that
* disagrees — which is how the actor pass found 18 drifted declarations.
* ⚠️ A type whose subject is genuinely absent takes `none`, not `varies`: `none`
* pins `resourceType` to `null` and is a positive claim, where `varies` is the
* absence of one.
*/
const AUDIT_EVENT_CLASSIFICATION = Object.freeze({
	token_refresh: {
		level: "system",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "session"
	},
	app_token_minted: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	app_opened: {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	user_record_viewed: {
		level: "activity",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "user"
	},
	login_success: {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "none"
	},
	login_failure: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "user"
	},
	logout: {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "session"
	},
	email_verified: {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "none"
	},
	password_changed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "none"
	},
	password_reset_requested: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "user"
	},
	password_reset_confirmed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "none"
	},
	recovery_code_failed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "none"
	},
	recovery_code_used: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "none"
	},
	operator_auth_recovery: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "user"
	},
	passkey_registered: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "passkey"
	},
	passkey_deleted: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "passkey"
	},
	passkey_login_success: {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "passkey"
	},
	passkey_login_failure: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "passkey",
		related: {
			keys: ["userId"],
			kind: "person"
		}
	},
	second_factor_success: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "passkey"
	},
	second_factor_failure: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "user"
	},
	passkey_enrolled_at_login: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "passkey"
	},
	recovery_session_started: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "none"
	},
	recovery_passkey_registered: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "passkey"
	},
	passkeys_admin_reset: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	second_factor_policy_changed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "organization"
	},
	kernel_session_started: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "session"
	},
	kernel_shell_session_issued: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "session",
		related: null
	},
	kernel_handoff_issued: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "session"
	},
	kernel_session_revoked: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "session"
	},
	kernel_handoff_redeem_failed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "session"
	},
	step_up_request_created: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "step_up_request"
	},
	step_up_request_approved: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "step_up_request",
		related: null
	},
	step_up_request_denied: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "step_up_request"
	},
	step_up_assertion_failed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "step_up_request"
	},
	step_up_request_consumed: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "step_up_request"
	},
	kernel_consent_granted: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "kernel_consent",
		related: null
	},
	kernel_consent_revoked: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "kernel_consent",
		related: null
	},
	oauth_login_success: {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	oauth_account_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "user"
	},
	oauth_domain_rejected: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "organization"
	},
	oauth_consent_approved: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "oauth_client",
		related: null
	},
	oauth_consent_denied: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "authorization_request"
	},
	oauth_grant_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "oauth_grant",
		related: null
	},
	oauth_grant_revoked: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "oauth_grant",
		related: null
	},
	oauth_grant_revoked_by_client: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "oauth_grant",
		related: null
	},
	oauth_grant_reuse_detected: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "oauth_grant",
		related: null
	},
	oauth_client_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "oauth_client",
		related: null
	},
	oauth_client_updated: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "oauth_client",
		related: null
	},
	oauth_client_deleted: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "oauth_client",
		related: null
	},
	oauth_client_self_registered: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "oauth_client",
		related: null
	},
	dcr_redirect_host_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dcr_redirect_host"
	},
	dcr_redirect_host_updated: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dcr_redirect_host"
	},
	dcr_redirect_host_deleted: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dcr_redirect_host"
	},
	dcr_redirect_host_rejected: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dcr_redirect_host"
	},
	dcr_redirect_host_update_failed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dcr_redirect_host"
	},
	dcr_redirect_host_delete_failed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dcr_redirect_host"
	},
	scim_user_created: {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "user"
	},
	scim_user_updated: {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "user"
	},
	scim_user_deleted: {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "user"
	},
	scim_group_created: {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "group"
	},
	scim_group_updated: {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "group"
	},
	scim_group_deleted: {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "group"
	},
	scim_token_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "scim_token"
	},
	scim_token_revoked: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "scim_token"
	},
	user_deactivated: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	user_reactivated: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	member_removed: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	role_changed: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	ownership_transferred: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "organization",
		related: {
			keys: ["newOwnerId"],
			kind: "person"
		}
	},
	role_definition_created: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "org_role_definition"
	},
	role_definition_deleted: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "org_role_definition"
	},
	role_definition_updated: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "org_role_definition"
	},
	user_role_granted: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "user",
		related: {
			keys: ["roleId"],
			kind: "role"
		}
	},
	user_role_revoked: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "user",
		related: {
			keys: ["roleId"],
			kind: "role"
		}
	},
	group_role_granted: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group",
		related: {
			keys: ["roleId"],
			kind: "role"
		}
	},
	group_role_revoked: {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group",
		related: {
			keys: ["roleId"],
			kind: "role"
		}
	},
	group_created: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group"
	},
	group_deleted: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group"
	},
	group_child_added: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group",
		related: {
			keys: ["childGroupId"],
			kind: "group"
		}
	},
	group_member_added: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group",
		related: {
			keys: ["principalId"],
			kind: "member"
		}
	},
	group_member_removed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group",
		related: {
			keys: ["principalId"],
			kind: "member"
		}
	},
	group_updated: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group"
	},
	group_child_removed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "group",
		related: {
			keys: ["childGroupId"],
			kind: "group"
		}
	},
	team_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "team"
	},
	org_updated: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "organization"
	},
	org_archived: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "organization"
	},
	invitation_created: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "invitation"
	},
	invitation_revoked: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "invitation"
	},
	invitation_accepted: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "user"
	},
	auth_provider_created: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "auth_provider"
	},
	auth_provider_updated: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "auth_provider"
	},
	auth_provider_deleted: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "auth_provider"
	},
	setup_completed: {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "organization"
	},
	override_created: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "fixture_version_override"
	},
	override_deleted: {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "fixture_version_override"
	},
	"machine_principal.enrolled": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal",
		related: null
	},
	"machine_principal.revoked": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal"
	},
	"machine_principal.suspended": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal",
		related: null
	},
	"machine_principal.resumed": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal",
		related: null
	},
	"machine_principal.roles_changed": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal"
	},
	"machine_principal.key_rotated": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "machine_principal",
		related: null
	},
	"machine_principal.token_minted": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "none"
	},
	"machine_principal.host_identity_substituted": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal"
	},
	"machine_principal.host_identity_substitution_denied": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal"
	},
	"machine_principal.service_bearer_denied": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "none"
	},
	"machine_principal.host_identity_attribute_converged": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "machine_principal",
		related: {
			keys: ["targetPrincipalId"],
			kind: "principal"
		}
	},
	"machine_principal.consumes_stream_principals_converged": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "machine_principal"
	},
	"machine_principal.rights_certificates_converged": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "machine_principal"
	},
	"machine_principal.agent_id_rebound": {
		level: "admin",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal",
		related: null
	},
	"machine_principal.key_orphan_compensation_failed": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "principal_key",
		related: {
			keys: ["principalId"],
			kind: "principal"
		}
	},
	"delegation.minted": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "delegation_grant",
		related: {
			keys: ["delegatedActorId"],
			kind: "member"
		}
	},
	"delegation.revoked": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "delegation_grant"
	},
	"delegation.exchanged": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "delegation_grant",
		related: {
			keys: ["runAsId"],
			kind: "member"
		}
	},
	"agent_capability.issued": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal"
	},
	"machine_principal.federated_exchange": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "none"
	},
	"machine_principal.enrolled_external": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal",
		related: null
	},
	"machine_principal.key_registered": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "principal_key",
		related: {
			keys: ["principalId"],
			kind: "principal"
		}
	},
	"machine_principal.key_revoked": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "principal_key",
		related: {
			keys: ["principalId"],
			kind: "principal"
		}
	},
	"machine_principal.enroll_token_created": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_principal",
		related: null
	},
	"machine_trust_policy.created": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_trust_policy",
		related: {
			keys: ["machinePrincipalId"],
			kind: "principal"
		}
	},
	"machine_trust_policy.updated": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_trust_policy",
		related: {
			keys: ["machinePrincipalId"],
			kind: "principal"
		}
	},
	"machine_trust_policy.disabled": {
		level: "security",
		actor: "varies",
		destination: "audit",
		expectedActorType: "varies",
		subject: "machine_trust_policy",
		related: {
			keys: ["machinePrincipalId"],
			kind: "principal"
		}
	},
	"instance.launched.by.user": {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "fixture_instance"
	},
	"instance.launched.by.fixture": {
		level: "activity",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "fixture_instance",
		related: {
			keys: ["ownerUserId"],
			kind: "person"
		}
	},
	"instance.destroyed": {
		level: "activity",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "fixture_instance"
	},
	"instance.idle-swept": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "fixture_instance"
	},
	"instance.channel-rolled": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "unset"
	},
	"instance.channel-repinned": {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "fixture_instance"
	},
	"app.permission.invalid": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "none",
		related: {
			keys: ["userId", "principalId"],
			kind: "member"
		}
	},
	"principal.role.unresolved": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "unknown",
		subject: "none",
		related: {
			keys: ["userId"],
			kind: "person"
		}
	},
	"app.permission.all_expansion_incomplete": {
		level: "system",
		actor: "system",
		destination: "loki",
		expectedActorType: null,
		subject: null
	},
	"notification.upgrade_started": {
		level: "system",
		actor: "system",
		destination: "loki",
		expectedActorType: null,
		subject: null
	},
	"notification.upgrade_completed": {
		level: "system",
		actor: "system",
		destination: "loki",
		expectedActorType: null,
		subject: null
	},
	"notification.upgrade_failed": {
		level: "system",
		actor: "system",
		destination: "loki",
		expectedActorType: null,
		subject: null
	},
	"app_permission_grant.revoke": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app_permission_grant",
		related: {
			keys: ["orgRoleId"],
			kind: "role"
		}
	},
	"app_platform_permission.grant": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app_platform_grant"
	},
	"app_platform_permission.revoke": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app_platform_grant",
		related: {
			keys: ["grantedBy"],
			kind: "person"
		}
	},
	"app_adoption.accepted": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	"app_adoption.paused": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	"app_adoption.resumed": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	"app_adoption.removed": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	"blob_bucket_settings.updated": {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "app"
	},
	"resource_grant.granted": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "resource_grant",
		related: {
			keys: ["orgRoleId", "subjectId"],
			kind: "grant_subject"
		}
	},
	"resource_grant.revoked": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "resource_grant",
		related: {
			keys: ["orgRoleId", "subjectId"],
			kind: "grant_subject"
		}
	},
	"dlq.replay": {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dlq_message"
	},
	"dlq.discard": {
		level: "admin",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dlq_message"
	},
	"dlq.cross_tenant_attempt": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "human",
		subject: "dlq_message",
		related: null
	},
	"mail.sent": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "mail"
	},
	"mail.failed": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "mail"
	},
	"mail.refused_limit": {
		level: "security",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "mail"
	},
	"mail.refused_disabled": {
		level: "system",
		actor: "system",
		destination: "audit",
		expectedActorType: "service",
		subject: "mail"
	},
	"customer_channel.source_changed": {
		level: "security",
		actor: "people",
		destination: "audit",
		expectedActorType: "varies",
		subject: "customer_channel"
	}
});
/**
* The ruled opt-outs of the related-id declaration, as data (2026-10-06-audit-second-id-filter,
* D-13, R-019, R-021). One entry for each event type whose entry in
* {@link AUDIT_EVENT_CLASSIFICATION} is `related: null`, and no other entry.
*
* Who calls it: the core-api census AC-13 in `audit-completeness.spec.ts`, a test (R-021). The
* census must import this object and must keep no copy of it. The tests of this package check the
* key set, the shape and the key names only. Only the census compares `keys` with the emit sites,
* in two directions:
* 1. An `Id` or `By` key at a literal emit site of an opt-out type that `keys` does not list is a
*    refusal. So a new identity key on an opted-out type needs a declaration or a ruling.
* 2. A key in `keys` that no emit site of the type writes is a refusal. So the list stays live and
*    shrinks when an emit site drops a key. A key is live when one of these sites writes it in an
*    object literal `details`:
*    - a literal emit site whose `eventType` the census resolves to the type;
*    - a site in one of the type's emit modules whose `eventType` the census does not resolve.
*      The census names each such site in AC-13a.
*    The second case keeps `machine_principal.enrolled` and `machine_principal.agent_id_rebound`
*    live with no hand exemption. Their one `enrolledBy` site in `MachineIdentityService` passes
*    the local `enrollEventType` as `eventType`. The one literal site of the first type, in
*    `ExternalPrincipalService`, writes no `Id` or `By` key.
*    The census reads the keys of a spread of object literals in `details` too, so a key that a
*    site writes through `...(x === undefined ? {} : { channelId: x })` counts as written.
*
* Each entry holds:
* - `keys`: the `details` keys that end in `Id` or `By` and name no second entity, at least one,
*   with no repeat, each `as const`.
* - `reason`: one or two sentences that name each key and the case of the `related` JSDoc that
*   admits it: the key names no identity of a related kind, or the key holds the actor's own id.
*
* Invariants: the type of the map's key set is the derived set of `related: null` types, through
* `satisfies`. So `tsc` refuses a missing entry, an extra entry and an entry for a type that
* declares `related` or has no `related` member. The object is frozen at the top level, as
* {@link AUDIT_EVENT_CLASSIFICATION} is. No emit site and no runtime path of any service reads it;
* the publisher reads only `related` on the class. It has no side effects and no error path.
*/
const AUDIT_RELATED_OPT_OUTS = Object.freeze({
	"machine_principal.enrolled": {
		keys: ["enrolledBy"],
		reason: "enrolledBy holds the id of the actor: MachineIdentityService writes it from the same grantedBy value it writes as actorId, so a userId read already matches the row on actor_id. The admin create in ExternalPrincipalService writes no id-like details key."
	},
	"machine_principal.agent_id_rebound": {
		keys: ["enrolledBy"],
		reason: "enrolledBy holds the id of the actor: the one emit site, in MachineIdentityService, is the machine_principal.enrolled site and writes it from the same grantedBy value it writes as actorId, so a userId read already matches the row on actor_id."
	},
	"dlq.cross_tenant_attempt": {
		keys: ["observedEnvelopeOrgId"],
		reason: "observedEnvelopeOrgId is the org id that a refused DLQ envelope claimed, and an org is no related kind."
	},
	kernel_consent_granted: {
		keys: ["appId", "codeSigningKeyId"],
		reason: "appId and codeSigningKeyId name the kernel app and its code-signing key. An app is out of scope (#3683), and a key is no related kind."
	},
	kernel_consent_revoked: {
		keys: ["appId", "codeSigningKeyId"],
		reason: "appId and codeSigningKeyId name the kernel app and its code-signing key. An app is out of scope (#3683), and a key is no related kind."
	},
	kernel_shell_session_issued: {
		keys: ["kernelFamilyId"],
		reason: "kernelFamilyId names the kernel session family that the shell family links to, and a session family is no related kind."
	},
	"machine_principal.enroll_token_created": {
		keys: ["enrollTokenId"],
		reason: "enrollTokenId names the enrol token row, and a token is no related kind."
	},
	"machine_principal.enrolled_external": {
		keys: ["enrollTokenId"],
		reason: "enrollTokenId names the enrol token row that the external principal redeemed, and a token is no related kind."
	},
	"machine_principal.suspended": {
		keys: ["appId"],
		reason: "appId names the app catalog entry that the principal belongs to, which the app-removal cut writes. An app is no related kind, and the appId filter is separate work (#3683)."
	},
	"machine_principal.resumed": {
		keys: ["appId"],
		reason: "appId names the app catalog entry that the principal belongs to, which the app-removal cut writes. An app is no related kind, and the appId filter is separate work (#3683)."
	},
	"machine_principal.key_rotated": {
		keys: ["oldKeyId", "newKeyId"],
		reason: "oldKeyId and newKeyId name the principal keys before and after the rotation, and a key is no related kind."
	},
	oauth_client_created: {
		keys: ["clientId"],
		reason: "clientId is the OAuth client string, and D-4 rules OAuth client strings out."
	},
	oauth_client_deleted: {
		keys: ["clientId"],
		reason: "clientId is the OAuth client string, and D-4 rules OAuth client strings out."
	},
	oauth_client_self_registered: {
		keys: ["clientId"],
		reason: "clientId is the OAuth client string, and D-4 rules OAuth client strings out."
	},
	oauth_client_updated: {
		keys: ["clientId"],
		reason: "clientId is the OAuth client string, and D-4 rules OAuth client strings out."
	},
	oauth_consent_approved: {
		keys: ["requestId", "channelId"],
		reason: "requestId names the consent request row, and a request is no related kind. channelId names the release channel that the consent picked, and a channel is no related kind."
	},
	oauth_grant_created: {
		keys: ["clientId"],
		reason: "clientId is the OAuth client string, and D-4 rules OAuth client strings out."
	},
	oauth_grant_reuse_detected: {
		keys: ["familyId"],
		reason: "familyId names the refresh token family, and a token family is no related kind."
	},
	oauth_grant_revoked: {
		keys: ["familyId"],
		reason: "familyId names the refresh token family, and a token family is no related kind."
	},
	oauth_grant_revoked_by_client: {
		keys: ["familyId"],
		reason: "familyId names the refresh token family, and a token family is no related kind."
	},
	step_up_request_approved: {
		keys: ["passkeyId"],
		reason: "passkeyId names the passkey that signed the approval, and D-4 rules passkey ids out."
	}
});
/** The classification for one event type, or {@link UNKNOWN_EVENT_CLASS}. */
function classifyEventType(eventType) {
	return Object.hasOwn(AUDIT_EVENT_CLASSIFICATION, eventType) ? AUDIT_EVENT_CLASSIFICATION[eventType] : UNKNOWN_EVENT_CLASS;
}
/**
* Whether `verb` names a member of the platform's own compiled audit vocabulary.
*
* ⛔ THE ONE CLOSED SET AN INGEST-DOOR `action` MAY NOT INTERSECT. It exists to
* close the level-evasion residual documented on {@link UNKNOWN_EVENT_CLASS}: a
* caller that names its row after a `system`-level platform type inherits that
* type's level and sits below the default view. Reject the collision and the
* verb is unknown instead, which resolves through {@link UNKNOWN_EVENT_CLASS} to
* `security`/`people` and is therefore always visible.
*
* ⛔ A PREDICATE OVER OUR SET, NOT AN ENUMERATION OF THEIRS. The ingest
* vocabulary is unbounded by design and no allowlist could cover it. This
* bounds the one thing that can be bounded: what a caller may not impersonate.
*
* ⚠️ DIRECTION OF FAILURE. If the platform vocabulary later gains a member that
* an app has already declared, this predicate starts rejecting that app's writes.
* That is deliberate and loud. Stage 2's publish validation rejects the same
* collision at declaration time (C-008), so the loud failure arrives at publish
* rather than at runtime for anything declared after stage 2.
*/
function isPlatformAuditVerb(verb) {
	return Object.hasOwn(AUDIT_EVENT_CLASSIFICATION, verb);
}
/** Rank on the ladder — higher is more severe. */
function levelRank(level) {
	return AUDIT_LEVELS.indexOf(level);
}
/**
* Every known event type ranking BELOW `floor` — i.e. the set a threshold of
* `floor` hides.
*
* ⛔ RETURNS AN EXCLUSION LIST, NOT AN INCLUSION LIST, AND THAT IS THE FAIL-SAFE
* DIRECTION. The predicate becomes `event_type NOT IN (…)`, so an event type
* this module has never heard of is RETURNED rather than filtered out — the same
* guarantee {@link UNKNOWN_EVENT_CLASS} states, expressed in SQL. An inclusion
* list would silently drop every unclassified type.
*/
function eventTypesBelowLevel(floor) {
	const min = levelRank(floor);
	return Object.entries(AUDIT_EVENT_CLASSIFICATION).filter(([, cls]) => levelRank(cls.level) < min).map(([type]) => type);
}
/**
* Every known event type whose actor is `actor`.
*
* A type whose actor class is `varies` (R-041) is in neither list. Its rows divide per row on
* `actor_type`, so a view resolves them with a row predicate and never with this type list.
*
* Used in both directions, and the direction differs by design: filtering to
* `people` EXCLUDES this set for `system` (so unknown types stay visible), while
* filtering to `system` INCLUDES this set (so unknown types are not invented
* into a machine-only view). Every row therefore remains REACHABLE, which is the
* property that matters.
*
* ⚠️ Reachable, not necessarily one click: 9 types are both below the `admin`
* floor AND actor `system`, so from the default view BOTH controls must move.
* `level=system, actor=all` always returns everything.
*/
function eventTypesWithActor(actor) {
	return Object.entries(AUDIT_EVENT_CLASSIFICATION).filter(([, cls]) => cls.actor === actor).map(([type]) => type);
}
//#endregion
export { AUDIT_ACTOR_FILTERS, AUDIT_ACTOR_TYPES, AUDIT_EVENT_CLASSIFICATION, AUDIT_LEVELS, AUDIT_LOG_SOURCES, AUDIT_RELATED_KINDS, AUDIT_RELATED_OPT_OUTS, AUDIT_VERB_PATTERN, DEFAULT_AUDIT_ACTOR, DEFAULT_AUDIT_LEVEL, UNKNOWN_EVENT_CLASS, classifyEventType, eventTypesBelowLevel, eventTypesWithActor, isPlatformAuditVerb, levelRank };

//# sourceMappingURL=audit-vocabulary.js.map